import { useState, useEffect, useRef } from 'react';
import { X, Upload } from 'lucide-react';
import { scrollToTop } from '../../hooks/useScrollToTop';

interface ShopModalProps {
  shop?: any;
  onClose: () => void;
  onSave: (shopData: any) => void;
}

export default function ShopModal({ shop, onClose, onSave }: ShopModalProps) {
  const [formData, setFormData] = useState({
    shopNumber: '',
    size: '',
    purpose: '',
    floor: 'Ground',
    status: 'available',
    floorplanUrl: ''
  });

  const [errors, setErrors] = useState<any>({});
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (shop) {
      setFormData({
        shopNumber: shop.shopNumber,
        size: shop.size.toString(),
        purpose: shop.purpose,
        floor: shop.floor,
        status: shop.status,
        floorplanUrl: shop.floorplanUrl || ''
      });
    }
  }, [shop]);

  // Scroll to top when modal opens
  useEffect(() => {
    scrollToTop(modalRef.current);
  }, []);

  const updateFormData = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };


  const validateForm = () => {
    const newErrors: any = {};
    
    if (!formData.shopNumber.trim()) newErrors.shopNumber = 'Shop number is required';
    if (!formData.size.trim()) newErrors.size = 'Size is required';
    if (!formData.purpose.trim()) newErrors.purpose = 'Purpose is required';
    if (formData.floorplanUrl && formData.floorplanUrl.trim() !== '') {
      const value = formData.floorplanUrl.trim();
      const isLocalPath = value.startsWith('/');
      if (!isLocalPath) {
        try {
          new URL(value);
        } catch {
          newErrors.floorplanUrl = 'Please provide a valid URL (https://...) or a local path (/image.jpg)';
        }
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const shopData = {
        ...formData,
        size: parseFloat(formData.size)
      };
      
      // Remove empty floorplanUrl to avoid validation issues
      if (!shopData.floorplanUrl || shopData.floorplanUrl.trim() === '') {
        delete shopData.floorplanUrl;
      }
      
      onSave(shopData);
    }
  };

  const purposeOptions = [
    'Pharmacy', 'Clothing Store', 'Confectionary', 'Electronics', 
    'Groceries', 'Beauty Salon', 'Bookstore', 'Sports Goods', 
    'Jewelry', 'Home & Garden', 'Office', 'Gaming Hub', 'Mens Wear', 
    'Womens Wear', 'Childrens Wear', 'Baby Wear', 'Shoes', 'Bags', 
    'Accessories', 'Watches', 'PPE', 'Photo Studio', 'Hand Bags', 
    'Uniforms & Corporate Ware', 'Chinese', 'Electric Appliances', 
    'Printing', 'Perfumes', 'Branding', 'Tailor', 'Linen', 'Braids & Hair Products', 'Deco & Homeware', 'Cosmetics', 'Diapers', 'Other'
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4">
      <div ref={modalRef} className="bg-white rounded-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">
            {shop ? 'Edit Shop' : 'Add New Shop'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Shop Number *
              </label>
              <input
                type="text"
                value={formData.shopNumber}
                onChange={(e) => updateFormData('shopNumber', e.target.value)}
                className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base ${
                  errors.shopNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., G-01"
              />
              {errors.shopNumber && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.shopNumber}</p>}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Size (sqm) *
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.size}
                onChange={(e) => updateFormData('size', e.target.value)}
                className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base ${
                  errors.size ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="45.5"
              />
              {errors.size && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.size}</p>}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Purpose *
              </label>
              <select
                value={formData.purpose}
                onChange={(e) => updateFormData('purpose', e.target.value)}
                className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base ${
                  errors.purpose ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select purpose</option>
                {purposeOptions.map(purpose => (
                  <option key={purpose} value={purpose}>{purpose}</option>
                ))}
              </select>
              {errors.purpose && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.purpose}</p>}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Floor
              </label>
              <select
                value={formData.floor}
                onChange={(e) => updateFormData('floor', e.target.value)}
                className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
              >
                <option value="Ground">Ground Floor</option>
                <option value="First">First Floor</option>
              </select>
            </div>


            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => updateFormData('status', e.target.value)}
                className="w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base"
              >
                <option value="available">Available</option>
                <option value="reserved">Reserved</option>
                <option value="rented">Rented</option>
                <option value="unavailable">Unavailable</option>
              </select>
            </div>

          </div>


          <div className="mt-4 sm:mt-6">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Floor Plan URL
            </label>
            <input
              type="url"
              value={formData.floorplanUrl}
              onChange={(e) => updateFormData('floorplanUrl', e.target.value)}
              className={`w-full px-2.5 sm:px-3 py-1.5 sm:py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm sm:text-base ${
                errors.floorplanUrl ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="/GAX_8388.jpg or https://example.com/image.jpg"
            />
            {errors.floorplanUrl && <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.floorplanUrl}</p>}
          </div>

          {/* Rental History Section */}
          {shop && shop.rentalHistory && shop.rentalHistory.length > 0 && (
            <div className="mt-6 sm:mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Rental History</h3>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {shop.rentalHistory.map((rental, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-medium text-gray-900">{rental.tenantName}</h4>
                        <p className="text-sm text-gray-600">{rental.companyName}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        rental.status === 'active' ? 'bg-green-100 text-green-800' :
                        rental.status === 'transferred' ? 'bg-blue-100 text-blue-800' :
                        rental.status === 'evicted' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {rental.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Start:</span> {new Date(rental.startDate).toLocaleDateString()}
                      </div>
                      <div>
                        <span className="font-medium">End:</span> {rental.endDate ? new Date(rental.endDate).toLocaleDateString() : 'Ongoing'}
                      </div>
                      <div>
                        <span className="font-medium">Rent:</span> ${rental.monthlyRent?.toFixed(2) || 'N/A'}
                      </div>
                      <div>
                        <span className="font-medium">Deposit:</span> ${rental.depositPaid?.toFixed(2) || 'N/A'}
                      </div>
                    </div>
                    {rental.reasonForLeaving && (
                      <div className="mt-2 text-sm">
                        <span className="font-medium text-gray-700">Reason for leaving:</span>
                        <span className="ml-1 text-gray-600">{rental.reasonForLeaving}</span>
                        {rental.reasonDetails && (
                          <p className="text-gray-600 mt-1">{rental.reasonDetails}</p>
                        )}
                      </div>
                    )}
                    {rental.notes && (
                      <div className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Notes:</span> {rental.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-6 sm:mt-8">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm sm:text-base"
            >
              {shop ? 'Update Shop' : 'Add Shop'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}